import {LogMarkerConsts} from '@multipass-common-dto';
import {FetchOptions, FetchResults,FetchService, MarkerUtil, MessagingClient, Operation, RequestOptions} from '@multipass-system';
import {Logger} from 'esp-js';
import * as uuid from 'uuid';

import {AppConfig} from '../shell-api';

const sessionId = uuid.v4();

const _log = Logger.create('MessagingClient');

const CORRELATION_ID_HEADER = 'X-Correlation-Id';

interface ResponseEnvelope<TPayload> {
    correlationId: string;
    isSuccess: boolean;
    payload: TPayload;
    error?: { code: string; message: string; detail?: string };
}

export class MessagingClientImpl implements MessagingClient {
    readonly #fetchService: FetchService;
    readonly #appConfig: AppConfig;

    constructor(fetchService: FetchService, appConfig: AppConfig) {
        this.#fetchService = fetchService;
        this.#appConfig = appConfig;
    }

    get sessionId(): string {
        return sessionId;
    }

    async request<TRequestDto, TResponseDto>(operation: Operation, dto: TRequestDto, options?: RequestOptions): Promise<TResponseDto> {
        _log.info(`${LogMarkerConsts.OutboundRestCall} Requesting ${operation.key}`);
        const workflowId = uuid.v4();
        const correlationId = uuid.v4();
        MarkerUtil
            .create()
            .appendOperationDetails(operation.name, workflowId, correlationId, this.sessionId);
        const headers = new Headers();
        headers.append(CORRELATION_ID_HEADER, correlationId);
        const isGet = operation.method.toUpperCase() === 'GET';
        const route = isGet ? interpolatePathParams(operation.route, dto) : operation.route;
        const fetchOptions: FetchOptions = {
            url: `${this.#appConfig.gatewayApiUrl}${route}`,
            method: operation.method as FetchOptions['method'],
            headers,
            body: isGet ? undefined : dto,
            workflowId: workflowId,
        };
        const fetchPromise = this.#fetchService.fetch<ResponseEnvelope<TResponseDto>>(fetchOptions).then(fetchResults => {
            _log.info(`Response received: ${JSON.stringify(fetchResults)}`);
            return unwrap(operation, fetchResults);
        });
        if (options?.timeoutMs == null) {
            return fetchPromise;
        }
        const timeoutMs = options.timeoutMs;
        return Promise.race([
            fetchPromise,
            new Promise<never>((_, reject) => setTimeout(() => reject(new Error(`Timeout of ${timeoutMs}ms elapsed waiting for operation '${operation.name}' to return`)), timeoutMs)),
        ]);
    }
}

const interpolatePathParams = <TRequestDto>(route: string, dto: TRequestDto): string => {
    const params = (dto ?? {}) as Record<string, unknown>;
    return route.replace(/\{(\w+)\}/g, (_match, key: string) => encodeURIComponent(String(params[key])));
};

const unwrap = <TResponseDto>(operation: Operation, fetchResults: FetchResults<ResponseEnvelope<TResponseDto>>): TResponseDto => {
    if (!fetchResults.ok) {
        throw new Error(`Request for operation '${operation.name}' failed: ${fetchResults.error ?? fetchResults.statusText}`);
    }
    const envelope = fetchResults.payload;
    if (!envelope) {
        throw new Error(`Request for operation '${operation.name}' returned an empty body`);
    }
    if (!envelope.isSuccess) {
        const error = envelope.error;
        throw new Error(`Operation '${operation.name}' returned an error: ${error ? `${error.code} - ${error.message}` : 'unknown'}`);
    }
    return envelope.payload;
};
